import { chatFileMediaType, isChatFilePreviewable } from '@zerohuman/shared'
import { onUnmounted, ref, shallowReactive } from 'vue'
import { api, type ChatAttachedFile } from '@/api'

/** What a chip says, in place of a broken image or an empty dialog, when its file's bytes are refused (#1734). */
export const CHAT_FILE_UNAVAILABLE = 'This file is no longer available.'

/** How long a file handed to a new tab or a download keeps its object URL: long enough for either to read it. */
export const HANDED_OFF_URL_MS = 60_000

/** A file shown in the chat's preview dialog: the full image, or a PDF in the browser's own viewer. */
export type ChatFilePreview = { file: ChatAttachedFile; url: string; image: boolean }

/** Whether a chat file is an image, which its chip shows as a thumbnail and its preview as the full picture. */
export function isChatImage(file: Pick<ChatAttachedFile, 'type'>): boolean {
    return file.type === 'png' || file.type === 'jpg'
}

/**
 * Whether a failed read was chat refusing the file (`chat_not_found`, as `requestBlob` throws it), which is the one
 * failure that means the file is gone. A dropped connection, a server error or a lapsed session is not that.
 */
export function isChatFileRefusal(error: unknown): boolean {
    return error instanceof Error && error.message === 'chat_not_found'
}

/**
 * The files of the chat on screen as people see them (#1734): a thumbnail for each image, the one file in the preview
 * dialog, and a file opened in a new tab or saved under its own name. Every byte is read through the portal's api
 * client as a Blob and shown through an object URL, never by pointing the browser at the API, so it works however the
 * session reaches the API.
 *
 * Each object URL is revoked once nothing shows it: a preview's when its dialog closes, the thumbnails when another
 * chat opens or the screen goes, and one handed to a new tab or a download a minute later, or when the screen goes.
 * A refused read marks the file unavailable, so its chip says so instead of drawing a broken image or opening an
 * empty dialog. Any other failed read (the connection, the server, the session) marks nothing, and a thumbnail that
 * failed that way is asked for again the next time the thread is read.
 *
 * `chatId` names the chat the files were attached in, read at the moment each file is asked for.
 */
export function useChatFiles(chatId: () => string) {
    /** Each image's thumbnail, by file id. */
    const thumbnails = shallowReactive(new Map<string, string>())
    /** The files whose bytes were refused the last time they were asked for. */
    const unavailable = shallowReactive(new Set<string>())
    const preview = ref<ChatFilePreview | null>(null)
    /** The thumbnails already asked for, so a thread read again does not fetch them again. */
    const asked = new Set<string>()
    /** The preview shows a URL of its own, rather than its image's thumbnail, and it goes when the dialog closes. */
    let previewOwned = false
    /** Each URL handed to a new tab or a download, with the timer that revokes it. */
    const handedOff = new Map<string, ReturnType<typeof setTimeout>>()
    /** Moves on when another chat opens or the screen goes: a read that lands after belongs to neither. */
    let epoch = 0

    /** An object URL of the file's bytes, or null when they are refused or no longer wanted. */
    async function objectUrl(file: ChatAttachedFile, asDownload: boolean): Promise<string | null> {
        const started = epoch
        let blob: Blob
        try {
            blob = await api.chatFile(chatId(), file.id, asDownload)
        } catch (error) {
            // Only chat's own refusal says the file is gone: a file that merely failed to arrive can still be read.
            if (started === epoch && isChatFileRefusal(error)) {
                unavailable.add(file.id)
            }
            return null
        }
        if (started !== epoch) {
            return null
        }
        unavailable.delete(file.id)
        // Shown as its own kind, whatever the response was labelled: a file is never drawn as a page.
        return URL.createObjectURL(asDownload ? blob : new Blob([blob], { type: chatFileMediaType(file.type) }))
    }

    /** Reads the thumbnail of each image among `files` not asked for yet. */
    function loadThumbnails(files: readonly ChatAttachedFile[]): void {
        for (const file of files) {
            if (!isChatImage(file) || asked.has(file.id)) {
                continue
            }
            asked.add(file.id)
            const started = epoch
            void objectUrl(file, false).then(url => {
                if (url !== null) {
                    thumbnails.set(file.id, url)
                } else if (started === epoch && !unavailable.has(file.id)) {
                    // It failed to arrive rather than being refused: the next thread read asks for it again.
                    asked.delete(file.id)
                }
            })
        }
    }

    /** Shows an image or a PDF in the preview dialog: an image from its thumbnail when it has one. */
    async function openPreview(file: ChatAttachedFile): Promise<void> {
        const thumbnail = thumbnails.get(file.id)
        const url = thumbnail ?? (await objectUrl(file, false))
        if (url === null) {
            return
        }
        closePreview()
        preview.value = { file, url, image: isChatImage(file) }
        previewOwned = thumbnail === undefined
    }

    function closePreview(): void {
        const shown = preview.value
        preview.value = null
        if (shown && previewOwned) {
            URL.revokeObjectURL(shown.url)
        }
        previewOwned = false
    }

    /**
     * Opens an image or a PDF in a new tab. A kind a browser cannot show is saved instead.
     *
     * The tab opens at the click, before the file is read: a browser lets a page open a tab only while the click
     * still counts as the person's own, and a large file on a slow connection takes longer than that to read. The
     * tab is pointed at the file once it is read, and closed if the read fails or another chat opened meanwhile. A
     * browser that blocks the tab even so gets the file saved instead, so Open never does nothing.
     */
    async function openInTab(file: ChatAttachedFile): Promise<void> {
        if (!isChatFilePreviewable(file.type)) {
            await download(file)
            return
        }
        const tab = window.open('', '_blank')
        if (!tab) {
            await download(file)
            return
        }
        // The tab only shows the file: it is given no way back to the portal's window.
        tab.opener = null
        const url = await objectUrl(file, false)
        if (url === null) {
            tab.close()
            return
        }
        tab.location.href = url
        handOff(url)
    }

    /** Saves the file under the name it was attached with, asking the server for a download. */
    async function download(file: ChatAttachedFile): Promise<void> {
        const url = await objectUrl(file, true)
        if (url === null) {
            return
        }
        const link = document.createElement('a')
        link.href = url
        link.download = file.filename
        document.body.append(link)
        link.click()
        link.remove()
        handOff(url)
    }

    function handOff(url: string): void {
        const timer = setTimeout(() => {
            handedOff.delete(url)
            URL.revokeObjectURL(url)
        }, HANDED_OFF_URL_MS)
        handedOff.set(url, timer)
    }

    /** Lets go of one chat's files as another opens: its thumbnails and preview go, and reads still out are dropped. */
    function reset(): void {
        epoch += 1
        closePreview()
        for (const url of thumbnails.values()) {
            URL.revokeObjectURL(url)
        }
        thumbnails.clear()
        unavailable.clear()
        asked.clear()
    }

    onUnmounted(() => {
        reset()
        for (const [url, timer] of handedOff) {
            clearTimeout(timer)
            URL.revokeObjectURL(url)
        }
        handedOff.clear()
    })

    return { thumbnails, unavailable, preview, loadThumbnails, openPreview, closePreview, openInTab, download, reset }
}
